import { useEffect, useRef, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { getApiBaseUrl } from '../config/runtimeEnv'

interface WebSocketHookOptions {
  onNewEmails?: (data: {
    newEmailsCount: number
    newEmails: any[]
    unreadCount: number
  }) => void
}

export function useWebSocket(options: WebSocketHookOptions = {}) {
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const socketRef = useRef<Socket | null>(null)
  const onNewEmailsRef = useRef(options.onNewEmails)

  // Keep the callback ref updated without triggering reconnection
  useEffect(() => {
    onNewEmailsRef.current = options.onNewEmails
  }, [options.onNewEmails])

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) {
      console.log('⚠️ No token available, skipping WebSocket connection')
      return
    }

    // Use same API base as api.ts (build-time or host-derived)
    const apiBaseUrl = getApiBaseUrl()
    let wsUrl = apiBaseUrl.endsWith('/api') ? apiBaseUrl.slice(0, -4) : apiBaseUrl
    
    console.log('🔌 Connecting to WebSocket...')
    console.log('   API Base URL:', apiBaseUrl)
    console.log('   WebSocket URL:', wsUrl)

    // Initialize Socket.io client with explicit configuration
    const socket = io(wsUrl, {
      auth: {
        token
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      path: '/socket.io',
      secure: true,
      rejectUnauthorized: false
    })

    socketRef.current = socket

    // Connection events
    socket.on('connect', () => {
      console.log('✅ WebSocket connected:', socket.id)
      setIsConnected(true)
      setError(null)
    })

    socket.on('authenticated', (data) => {
      console.log('🔐 WebSocket authenticated:', data)
    })

    socket.on('disconnect', (reason) => {
      console.log('❌ WebSocket disconnected:', reason)
      setIsConnected(false)
    })

    socket.on('connect_error', (err) => {
      console.error('❌ WebSocket connection error:', err.message)
      setError(err.message)
      setIsConnected(false)
    })

    socket.on('auth_error', (data) => {
      console.error('❌ WebSocket auth error:', data)
      setError(data.message)
    })

    // Gmail-specific events - use ref to avoid dependency issues
    socket.on('gmail_new_messages', (data) => {
      console.log('📧 New Gmail messages received via WebSocket:', data)
      
      if (onNewEmailsRef.current) {
        onNewEmailsRef.current({
          newEmailsCount: data.newEmailsCount,
          newEmails: data.newEmails,
          unreadCount: data.unreadCount
        })
      }
    })

    // Cleanup on unmount ONLY
    return () => {
      console.log('🔌 Disconnecting WebSocket...')
      socket.disconnect()
    }
  }, []) // Empty deps - only run once on mount

  return {
    isConnected,
    error,
    socket: socketRef.current
  }
}

